import React, { useState, useEffect } from 'react';
import { useEditor } from '@grapesjs/react';
import { Checkbox, FormControl, MenuItem, Select, SelectChangeEvent, TextField } from '@mui/material';
import type { Trait } from 'grapesjs';
import { View } from 'react-native';
import { Text } from '@constants/Themed';
import { Button } from 'tamagui';
import { debounce } from 'lodash';

interface TraitPropertyFieldProps {
  trait: Trait;
}

export default function TraitPropertyField({ trait, ...rest }: TraitPropertyFieldProps) {
  const editor = useEditor();
  const type = trait.getType();
  const defValue = trait.getDefault() || trait.attributes.placeholder;
  const [inputValue, setInputValue] = useState(trait.getValue() || defValue);

  // Function to update the trait in GrapesJS after a small delay (debouncing)
  const updateTraitValue = debounce((newValue: string) => {
    trait.setValue(newValue);
  }, 300); // 300ms delay prevents excessive updates

  useEffect(() => {
    setInputValue(trait.getValue() || defValue);
  }, [trait]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const newValue = event.target.value;
    setInputValue(newValue); // Update local state
    updateTraitValue(newValue); // Debounced update
  };

  const handleSelectChange = (event: SelectChangeEvent) => {
    const newValue = event.target.value;
    setInputValue(newValue);
    trait.setValue(newValue); // No need to debounce for select
  };

  const handleCheckboxChange = () => {
    const newValue = !trait.getValue();
    setInputValue(newValue ? 'true' : 'false');
    trait.setValue(newValue);
  };

  const handleButtonClick = () => {
    const command = trait.get('command');
    if (command) {
      typeof command === 'string' ? editor.runCommand(command) : command(editor, trait);
    }
  };

  let inputToRender;
  switch (type) {
    case 'select':
      inputToRender = (
        <FormControl fullWidth size="small">
          <Select value={inputValue} onChange={handleSelectChange}>
            {trait.getOptions().map((option) => (
              <MenuItem key={trait.getOptionId(option)} value={trait.getOptionId(option)}>
                {trait.getOptionLabel(option)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
      break;
    case 'color':
      inputToRender = (
        <input
          type="color"
          value={inputValue}
          onChange={handleChange}
          style={{
            width: 30,
            height: 30,
            borderRadius: '50%',
            cursor: 'pointer',
          }}
        />
      );
      break;
    case 'checkbox':
      inputToRender = <Checkbox checked={inputValue === 'true'} onChange={handleCheckboxChange} size="small" />;
      break;
    case 'button':
      inputToRender = <Button onPress={handleButtonClick} style={{ minWidth: '100%' }}>{trait.getLabel()}</Button>;
      break;
    default:
      inputToRender = (
        <TextField
          placeholder={defValue}
          value={inputValue}
          onChange={handleChange}
          size="small"
          fullWidth
        />
      );
  }

  return (
    <View {...rest} style={{ marginVertical: 3, paddingHorizontal: 1, width: '100%' }}>
      <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 2 }}>
        <Text style={{ flexGrow: 1 }}>{trait.getLabel()}</Text>
      </View>
      {inputToRender}
    </View>
  );
}
